import React, { useCallback, useEffect, useState } from 'react'
import { Route, Routes, useNavigate, useParams } from 'react-router-dom'
import Module from '../../../../layout/Module'
import AlertSaveButton from '../../../../button/AlertSaveButton'
import BasicInformation from './BasicInformation/BasicInformation'
import { Alert, Box } from '@mui/material'
import TabsLink from '../../../../tabs/TabsLink'
import { useDispatch, useSelector } from 'react-redux'
import BulkMarking from './BulkMarking/BulkMarking'
import StudentView from './StudentView/StudentView'
import GuttmanChart from './GuttmanChart/GuttmanChart'
import { handlerErrorV2 } from '../../../../../utils/errors'
import { getUnitOfLearningsClassById } from '../../../../../services/classes/classes.service'
import { updateClassForm, updateUnitOfLearningForm } from '../../../../../store/actions/class.actions'
import {
  clearClassMarking,
  updateClassMarkingCompletedType,
  updateDashboardLearningContinuum,
  updateUnitOfLearningStudents
} from '../../../../../store/actions/class-marking.actions'
import Loading from '../../../../Loading/Loading'
import SaveButtonStudentView from './SaveButton/SaveButton'
import StepsButtonBulkMarking from './StepsButton/StepsButton'
import SelectLearningContinuumMultiple from './SelectLearningContinuum/SelectLearningContinuumMultiple'
import SelectLearningContinuumSimple from './SelectLearningContinuum/SelectLearningContinuumSimple'
import SwitchGuttmanChart from './SwitchGuttmanChart/SwitchGuttmanChart'
import useDate from '../../../../../hooks/app/useDate'
import useMarking from '../../../../../hooks/maestro/useMarking'
import SelectorCompleteType from './SelectCompleteType/SelectorCompleteType'
import ModeMarking from './ModeMarking/ModeMarking'
import MarkingClassesProvider from './MarkingClassesContext/MarkingClassesProvider'
import Dashboard from './Dashboard/Dashboard'
import LearningGrowth from './LearningGrowth/LearningGrowth'
import { setCurrentCurriculum } from '../../../../../store/actions/assessment.actions'
import GroupMarking from './GroupMarking/GroupMarking'
import SaveButtonGroup from './SaveButtonGroup/SaveButtonGroup'
import HideLevels from './Hidelevels/HideLevels'
import PrintRubricButton from './PrintRubric/PrintRubricButton'
import SaveButtonForStudentStudentView from './SaveButtonForStudentStudentView/SaveButtonForStudentStudentView'

const MarkingClasses = () => {
  const navigate = useNavigate()
  const { determinateStatus } = useDate()
  const { getStudentsMarkingAllByClassIdAndUlId } = useMarking()

  const { classId, studentId, curriculumId, ulId, ...params } = useParams()
  const type = params['*']
  const userRolesSession = useSelector((state) => state?.session?.user?.roles?.map((role) => role.id))
  const permissions = useSelector((state) => state?.session?.permissions?.['marking-class'] || {})
  const redirectStudentId = studentId ? `${studentId}/` : ''
  const current = useSelector((state) => state?.assessment?.currentCurriculum)
  const ulData = useSelector((state) => state?.class?.unitOfLearning || {})
  const mode = useSelector((state) => state?.classMarking?.mode || [])
  const pathname = window.location.pathname
  const groupMarkingStudent = useSelector((state) => state?.classMarking?.groupMarkingStudent || [])

  const onCancel = () => {
    navigate(`/dashboard/${redirectStudentId}classes/${curriculumId}/preview/${classId}`)
    dispatch(clearClassMarking())
  }

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const dispatch = useDispatch()

  const onLoad = useCallback(async () => {
    if (classId && curriculumId && ulId) {
      setLoading(true)
      dispatch(clearClassMarking())
      try {
        const response = await getUnitOfLearningsClassById(curriculumId, classId, ulId)
        if (!response?.data && response?.error) {
          setError(response.error.message || 'An error ocurred')
        } else {
          const formData = response?.data || {}
          const ul = formData.unitOfLearnings.find((ul) => ul._id === ulId)
          if (ul) {
            ul.status = determinateStatus(ul.startDate, ul.endDate)
            formData.students =
              formData.students.map((item, index) => ({
                _id: item._id,
                studentId: item._id,
                code: item.code,
                fullName: `${item.preferredName || item.firstName} ${item.lastName}`,
                preferredName: item.preferredName,
                firstName: item.firstName,
                lastName: item.lastName,
                photo: item.photo,
                index,
                value: item._id,
                label: `${item.code} - ${item.preferredName || item.firstName} ${item.lastName}`
              })) || []
            dispatch(updateClassForm(formData))
            dispatch(updateUnitOfLearningForm(ul))
            dispatch(updateUnitOfLearningStudents(formData.students))
            if (permissions['by-teacher'] || permissions['guttman-chart']) {
              getStudentsMarkingAllByClassIdAndUlId(formData.students, ul, current)
            }
            const firstLcId = ul.lcs[0]?._id
            if (firstLcId) {
              dispatch(updateDashboardLearningContinuum([firstLcId]))
            }
          }
        }
      } catch (error) {
        const message = handlerErrorV2(error)
        dispatch(clearClassMarking())
        setError(message)
      } finally {
        setLoading(false)
      }
    }
  }, [curriculumId, ulId, classId, current])

  useEffect(() => {
    dispatch(setCurrentCurriculum(curriculumId))
  }, [curriculumId])

  useEffect(() => {
    if (
      pathname ===
      `/dashboard/${redirectStudentId}classes/${curriculumId}/preview/${classId}/unit-of-learning/${ulId}`
    ) {
      if (permissions['dashboard']) {
        navigate(
          `/dashboard/${redirectStudentId}classes/${curriculumId}/preview/${classId}/unit-of-learning/${ulId}`
        )
      } else {
        navigate(
          `/dashboard/${redirectStudentId}classes/${curriculumId}/preview/${classId}/unit-of-learning/${ulId}/student-view`
        )
      }
    }
  }, [])

  useEffect(() => {
    onLoad()
  }, [onLoad])

  useEffect(() => {
    if (permissions['by-student'] && permissions['by-teacher']) {
      dispatch(updateClassMarkingCompletedType('completedTeacher'))
    } else if (permissions['by-student']) {
      dispatch(updateClassMarkingCompletedType('completedStudent'))
    } else {
      dispatch(updateClassMarkingCompletedType('completedTeacher'))
    }
  }, [permissions])
  const width = 710

  return (
    <MarkingClassesProvider>
      <Module.BodyHeader
        className='border-b border-b-2 border-primary flex justify-between relative'
        height={'h-14'}
        py={0}
        px={0}
      >
        <BasicInformation isLoading={loading} />
        <div className='flex items-center justify-end gap-2 pr-4'>
          {!loading && permissions['print'] && <PrintRubricButton />}
          <AlertSaveButton
            submodule='Classes'
            disabled={loading}
            icon={['fal', 'times']}
            title='Cancel'
            color='primary'
            variant='outlined'
            className='shadow-sm bg-background'
            onDontSave={onCancel}
          />
        </div>
      </Module.BodyHeader>
      <Module.BodyContainer
        classNameWrapper={`${loading ? '' : 'overflow-auto'} ${
          error ? 'flex items-center justify-center' : ''
        }`}
      >
        {error ? (
          <Alert severity='error'>{error}</Alert>
        ) : loading ? (
          <Loading />
        ) : (
          <>
            <Box
              className='flex relative gap-4 h-10 border-b border-primary'
              style={{ height: 'calc(2.5rem - 2px)' }}
            >
              <div className='relative' style={{ width }}>
                <TabsLink
                  id={`/dashboard/${redirectStudentId}classes/${curriculumId}/preview/${classId}`}
                  className='bottom--1px'
                  items={[
                    {
                      id: 'dashboard',
                      name: 'Dashboard',
                      path: `/dashboard/${redirectStudentId}classes/${curriculumId}/preview/${classId}/unit-of-learning/${ulId}`,
                      permission: permissions['dashboard'],
                      end: true,
                      disabled: mode?.length > 0 || groupMarkingStudent.length > 0
                    },
                    {
                      id: 'student-view',
                      name: 'Student view',
                      path: `/dashboard/${redirectStudentId}classes/${curriculumId}/preview/${classId}/unit-of-learning/${ulId}/student-view`,
                      permission: permissions['student-view'],
                      end: true,
                      disabled: groupMarkingStudent.length > 0
                    },
                    {
                      id: 'learning-growth',
                      name: 'Learning growth',
                      path: `/dashboard/${redirectStudentId}classes/${curriculumId}/preview/${classId}/unit-of-learning/${ulId}/learning-growth`,
                      permission: permissions['learning-growth'],
                      end: true,
                      disabled: mode?.length > 0 || groupMarkingStudent.length > 0
                    },
                    {
                      id: 'bulk-marking',
                      name: 'Bulk marking',
                      path: `/dashboard/${redirectStudentId}classes/${curriculumId}/preview/${classId}/unit-of-learning/${ulId}/bulk-marking`,
                      permission: permissions['bulk'],
                      disabled:
                        ulData.status === 'inactive' || mode?.length > 0 || groupMarkingStudent.length > 0,
                      end: true
                    },
                    {
                      id: 'group-marking',
                      name: 'Group marking',
                      path: `/dashboard/${redirectStudentId}classes/${curriculumId}/preview/${classId}/unit-of-learning/${ulId}/group-marking`,
                      permission: permissions['group-marking'],
                      disabled: ulData.status === 'inactive' || mode?.length > 0,
                      end: true
                    },
                    {
                      id: 'guttman-chart',
                      name: 'guttman chart',
                      path: `/dashboard/${redirectStudentId}classes/${curriculumId}/preview/${classId}/unit-of-learning/${ulId}/guttman-chart`,
                      permission: permissions['guttman-chart'],
                      end: true,
                      disabled: mode?.length > 0 || groupMarkingStudent.length > 0
                    }
                  ]}
                />
              </div>
              <div
                className='absolute right-0 bg-background inset-y-0 grow'
                style={{ left: `calc(${width}px + 0.5rem)` }}
              >
                <div className='flex gap-2 items-center justify-between pr-4'>
                  <div className='flex items-center gap-2'>
                    {['student-view', 'bulk-marking', 'learning-growth'].includes(type) ? (
                      <SelectLearningContinuumMultiple />
                    ) : (
                      <div />
                    )}
                    {type === '' && permissions['dashboard'] ? (
                      <SelectLearningContinuumSimple />
                    ) : ['group-marking'].includes(type) && permissions['group-marking'] ? (
                      <SelectLearningContinuumSimple />
                    ) : (
                      <div />
                    )}
                    {['student-view', 'learning-growth', 'guttman-chart'].includes(type) ? (
                      <SelectorCompleteType type={type} />
                    ) : (
                      <div />
                    )}
                    {['student-view', 'bulk-marking', 'group-marking'].includes(type) && <HideLevels />}
                  </div>
                  <div className='flex items-center justify-end'>
                    {type === 'student-view' && permissions['student-view'] && ulData.status === 'active' && (
                      <SaveButtonStudentView />
                    )}
                    {type === 'bulk-marking' && permissions['bulk'] && ulData.status === 'active' && (
                      <StepsButtonBulkMarking />
                    )}
                    {type === 'group-marking' &&
                      permissions['group-marking'] &&
                      ulData.status === 'active' && <SaveButtonGroup />}
                    {type === 'guttman-chart' && permissions['guttman-chart'] && <SwitchGuttmanChart />}
                  </div>
                </div>
              </div>
            </Box>
            <ModeMarking />
            {userRolesSession?.[0] === 'Student' && <SaveButtonForStudentStudentView />}

            <Box className='absolute top-10 bottom-0 inset-x-0'>
              <Routes>
                <Route index path='/' element={<>{permissions['dashboard'] ? <Dashboard /> : <div />}</>} />
                <Route
                  path='/learning-growth'
                  element={permissions['learning-growth'] ? <LearningGrowth /> : <div />}
                />
                <Route
                  index
                  path='/student-view'
                  element={permissions['student-view'] ? <StudentView /> : <div />}
                />
                <Route path='/bulk-marking' element={permissions['bulk'] ? <BulkMarking /> : <div />} />
                <Route
                  path='/group-marking'
                  element={permissions['group-marking'] ? <GroupMarking /> : <div />}
                />
                <Route
                  path='/guttman-chart'
                  element={permissions['guttman-chart'] ? <GuttmanChart /> : <div />}
                />
              </Routes>
            </Box>
          </>
        )}
      </Module.BodyContainer>
    </MarkingClassesProvider>
  )
}

export default MarkingClasses
